import { Geolocation } from '@capacitor/geolocation';
import axiosInstance from '../api/axiosInstance';

const STORAGE_KEY = 'trippin.capturePlaceContext.v1';
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_DISTANCE_METERS = 120;
const TEST_CAPTURE_COORDS = {
  latitude: 56.947627167444296,
  longitude: 24.113548919996603,
};

let memoryCache = null;
let inFlightContextPromise = null;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const hasCoordinates = (context) => (
  toNumber(context?.latitude) !== null && toNumber(context?.longitude) !== null
);

const normalizeContext = (context) => {
  if (!context || !hasCoordinates(context)) {
    return null;
  }

  return {
    latitude: toNumber(context.latitude),
    longitude: toNumber(context.longitude),
    reverseGeocodeResult: context.reverseGeocodeResult || null,
    fetchedAt: Number.isFinite(Number(context.fetchedAt)) ? Number(context.fetchedAt) : Date.now(),
    validatedAt: Number.isFinite(Number(context.validatedAt)) ? Number(context.validatedAt) : Date.now(),
  };
};

const readStoredContext = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return normalizeContext(JSON.parse(raw));
  } catch (error) {
    console.warn('[capturePlaceContext] Failed to read cache', error);
    return null;
  }
};

const persistContext = (context) => {
  const normalized = normalizeContext(context);
  if (!normalized) {
    return null;
  }

  memoryCache = normalized;

  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      console.warn('[capturePlaceContext] Failed to persist cache', error);
    }
  }

  return normalized;
};

const getCachedContext = () => {
  if (!memoryCache) {
    memoryCache = readStoredContext();
  }

  return memoryCache;
};

const isFresh = (context, now = Date.now()) => (
  Boolean(context?.fetchedAt) && now - context.fetchedAt <= CACHE_TTL_MS
);

const toRadians = (value) => value * (Math.PI / 180);

const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const earthRadius = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

const canReuseCachedPlace = (context, latitude, longitude, now = Date.now()) => {
  if (!isFresh(context, now)) {
    return false;
  }

  const cachedLatitude = toNumber(context?.latitude);
  const cachedLongitude = toNumber(context?.longitude);
  const nextLatitude = toNumber(latitude);
  const nextLongitude = toNumber(longitude);

  if (cachedLatitude === null || cachedLongitude === null || nextLatitude === null || nextLongitude === null) {
    return false;
  }

  return getDistanceMeters(cachedLatitude, cachedLongitude, nextLatitude, nextLongitude) <= CACHE_DISTANCE_METERS;
};

const fetchReverseGeocode = async (latitude, longitude) => {
  const { data } = await axiosInstance.post('/reverse-geocode/initiate', { latitude, longitude });
  return data || null;
};

export const getCachedCapturePlaceContext = () => getCachedContext();

export const primeCapturePlaceContext = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && inFlightContextPromise) {
    return inFlightContextPromise;
  }

  inFlightContextPromise = (async () => {
    const cached = getCachedContext();

    try {
      const latitude = TEST_CAPTURE_COORDS.latitude;
      const longitude = TEST_CAPTURE_COORDS.longitude;
      // const position = await Geolocation.getCurrentPosition({
      //   enableHighAccuracy: true,
      //   maximumAge: 60 * 1000,
      //   timeout: 10000,
      // });
      // const latitude = position.coords.latitude;
      // const longitude = position.coords.longitude;
      const now = Date.now();

      if (!forceRefresh && cached && canReuseCachedPlace(cached, latitude, longitude, now)) {
        return persistContext({
          ...cached,
          latitude,
          longitude,
          validatedAt: now,
        });
      }

      const reverseGeocodeResult = await fetchReverseGeocode(latitude, longitude);

      return persistContext({
        latitude,
        longitude,
        reverseGeocodeResult,
        fetchedAt: now,
        validatedAt: now,
      });
    } catch (error) {
      if (!forceRefresh && cached && isFresh(cached)) {
        return cached;
      }

      throw error;
    }
  })().finally(() => {
    inFlightContextPromise = null;
  });

  return inFlightContextPromise;
};

export const getCapturePlaceContext = async ({ prefetchedContext = null } = {}) => {
  const normalizedPrefetchedContext = normalizeContext(prefetchedContext);
  const prefetchedLatitude = toNumber(normalizedPrefetchedContext?.latitude);
  const prefetchedLongitude = toNumber(normalizedPrefetchedContext?.longitude);

  if (normalizedPrefetchedContext?.reverseGeocodeResult) {
    return normalizedPrefetchedContext;
  }

  if (inFlightContextPromise) {
    try {
      return await inFlightContextPromise;
    } catch (error) {
      if (normalizedPrefetchedContext) {
        return normalizedPrefetchedContext;
      }
    }
  }

  const cached = getCachedContext();
  if (cached && isFresh(cached)) {
    if (
      prefetchedLatitude === null ||
      prefetchedLongitude === null ||
      canReuseCachedPlace(cached, prefetchedLatitude, prefetchedLongitude)
    ) {
      return cached;
    }
  }

  if (normalizedPrefetchedContext) {
    return normalizedPrefetchedContext;
  }

  return primeCapturePlaceContext();
};